import { BASE_TASK_STATUS } from '@/constants';
import dayjs from 'dayjs';

const currentYear = new Date().getFullYear();
export const findModuleByPath = (path: string, routes: Array<any>) => {
  const firstSegment = '/' + path.split('/').filter(Boolean)[0];
  const module = routes.find(r => r.path === firstSegment);
  return module;
};

export const optionFilterForSelect = (input: string, option: any) => {
  const strVNForSearch = (str: any) =>
    str
      ? str
          .toString()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
      : '';

  const label = option?.label ?? option?.props?.children ?? option?.props?.value;

  return strVNForSearch(label).includes(strVNForSearch(input));
};

export const convertToAlias = (str: string): string => {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase();
};

export const removeNullFields = (obj: any): any => {
  const cleanObj: any = {};
  Object.keys(obj).forEach(key => {
    if (obj[key] !== null && obj[key] !== undefined && obj[key] !== '') {
      cleanObj[key] = obj[key];
    }
  });
  return cleanObj;
};

//currency input with max value, format with comma, prevent non-numeric input
export const formatCurrencyInput = (
  e: React.ChangeEvent<HTMLInputElement>,
  max: number = 1000000000
) => {
  // Chỉ cho phép nhập số
  let value = e.target.value.replace(/\D/g, '');

  if (!value) {
    e.target.value = '';
    return;
  }

  // Giới hạn max
  let num = Number(value);
  if (num > max) num = max;

  // Hiển thị có dấu phẩy
  e.target.value = num.toLocaleString('en-US');
};

export const currencyToNumber = (value: string) => {
  return Number(value.replace(/,/g, ''));
};

export const numberToCurrency = (value: number) => {
  return value.toLocaleString('en-US');
};

export const inputNumberWithLimitLength = (e: any, maxLength: number) => {
  e.target.value = e.target.value.replace(/\D/g, '');
  if (e.target.value.length > maxLength) {
    e.target.value = e.target.value.slice(0, maxLength);
  }
};

// Chỉ cho phép nhập số nguyên dương (không bao gồm 0)
export const inputPositiveInteger = (e: React.ChangeEvent<HTMLInputElement>) => {
  // Loại bỏ tất cả ký tự không phải số
  let value = e.target.value.replace(/\D/g, '');

  // Nếu giá trị là 0 hoặc rỗng, đặt về rỗng
  if (value === '0' || value === '') {
    e.target.value = '';
    return;
  }

  // Chỉ giữ lại số nguyên dương
  e.target.value = value;
};

// Chỉ cho phép nhập số nguyên dương có giới hạn (không bao gồm 0)
export const inputPositiveIntegerWithLimit = (
  e: React.ChangeEvent<HTMLInputElement>,
  max?: number
) => {
  // Loại bỏ tất cả ký tự không phải số
  let value = e.target.value.replace(/\D/g, '');

  // Nếu giá trị là 0 hoặc rỗng, đặt về rỗng
  if (value === '0' || value === '') {
    e.target.value = '';
    return;
  }

  // Nếu có giới hạn và giá trị vượt quá, cắt về giới hạn
  if (max !== undefined && Number(value) > max) {
    value = max.toString();
  }

  // Chỉ giữ lại số nguyên dương
  e.target.value = value;
};

export const getFileExtensionFromName = (filename: string): string => {
  const parts = filename.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
};

export const formatFileSize = (bytes: number): string => {
  if (!bytes) return '';
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

export const downloadFile = (blob: any, filename?: string) => {
  if (blob instanceof Blob) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'file';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    return;
  }
};

//for change status histories
export const splitForChangeStatusHistories = (str: string) => {
  if (!str) return ['', ''];
  const index = str.indexOf('/');
  if (index === -1) return [str, '']; // nếu không có "/"
  return [str.substring(0, index), str.substring(index + 1)];
};

export const replaceStatusForHistories = (str: string) => {
  const statusLabels = BASE_TASK_STATUS.map(status => status.label);

  let result = str;

  // Tìm và thay thế từng trạng thái bằng thẻ <strong>
  statusLabels.forEach(statusLabel => {
    const regex = new RegExp(statusLabel, 'g');
    result = result?.replace(regex, `<strong>${statusLabel}</strong>`);
  });

  return result;
};

export const timeFormat = ({
  date,
  showDate = true,
  showTime = true,
}: {
  date: string | null;
  showDate?: boolean;
  showTime?: boolean;
}) => {
  if (!date) return '';
  const d = new Date(date);
  let str = '';

  if (showDate) {
    str += d.toLocaleDateString('vi-VN');
  }
  if (showTime) {
    if (str) str += ' ';
    str += d.toLocaleTimeString('vi-VN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }
  return str.trim();
};

export const calculateDiffDays = (startDate: string, endDate: string) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  return end.diff(start, 'day') + 1;
};

export const convertPhoneToZeroFormat = (phone: string): string => {
  if (!phone) return '';

  const cleanPhone = phone.replace(/\s+/g, '').replace(/[^\d+]/g, '');

  if (cleanPhone.startsWith('+84')) {
    return '0' + cleanPhone.substring(3);
  }

  if (cleanPhone.startsWith('84') && cleanPhone.length > 2) {
    return '0' + cleanPhone.substring(2);
  }

  if (cleanPhone.startsWith('0')) {
    return cleanPhone;
  }

  return cleanPhone;
};

export const shallowEqual = (a: any, b: any): boolean => {
  if (a === b) return true;

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  return keysA.every(key => {
    const valA = a[key];
    const valB = b[key];

    // So sánh array
    if (Array.isArray(valA) && Array.isArray(valB)) {
      return valA.length === valB.length && valA.every((item, i) => item === valB[i]);
    }

    return valA === valB;
  });
};
